import { Dataset, PlanTypes, Question, Role } from './models/models';

export interface AppEnvironmentInterface {
  Environment: string;
  IntercomAppId: string;
  Api: ApiInterface;
}

export interface ApiInterface {
  CONFIG_HOST: string;
  SUSI_HOST: string;
  CORE_USERS_HOST: string;
  ANALYTICS_HOST: string;
  CURRENT_PLATFORM_HOST: string;
}

export enum EnvironmentEnum {
  Development = 'development',
  Testing = 'testing',
  Staging = 'staging',
  Production = 'production'
}

export enum OpenAccessHostURL {
  development = 'localhost:4200',
  testing = 'dashboards-testing.gwi.com',
  staging = 'dashboards-staging.gwi.com',
  production = 'dashboards.gwi.com'
}

export enum TokenType {
  Authorization = 'auth',
  Refresh = 'refresh'
}

export type Blank = null | undefined;

export type AppName = 'ChartBuilder' | 'AudienceBuilder' | 'CrosstabBuilder' | 'Dashboards' | 'TVRF';

export interface Widget {
  chart_type: DashboardChartType;
  name: string;
  height: number;
  width: number;
  position: number;
  data?: Array<WidgetData>;
  rich_content?: WidgetRichContent;
}

export interface WidgetData {
  question: string;
  metric: Metric;
}

export interface WidgetRichContent {
  paragraphs: Array<WidgetRichContentParagraph>;
}

export interface WidgetRichContentParagraph {
  content: string;
}

export interface Stats {
  positive_sample: number;
  positive_size: number;
  audience_sample: number;
  audience_size: number;
  audience_percentage: number;
  audience_index: number;
  datapoint_sample: number;
  datapoint_size: number;
  datapoint_percentage: number;
}

export enum DashboardChartType {
  DataTable = 'data_table',
  DivergingBarChart = 'diverging_bar_chart',
  DonutChart = 'donut_chart',
  HorizontalBarChart = 'horizontal_bar_chart',
  LineChart = 'line_chart',
  PieChart = 'pie_chart',
  PieRadarChart = 'pie_radar_chart',
  RadarChart = 'radar_chart',
  RadialBarChart = 'radial_bar_chart',
  StatCard = 'stat_card',
  StatCardColored = 'stat_card_colored',
  VerticalBarChart = 'vertical_bar_chart'
}

export enum Metric {
  AudiencePercentage = 'percentage',
  DataPointPercentage = 'horizontal_percentage',
  Universe = 'weighted_universe_count',
  Index = 'index',
  Responses = 'responses_count'
}

export class GWIPackageKeys {
  public static P2Lib = '@globalwebindex/platform2-lib';
  public static RUC = '@globalwebindex/platform2-react-ui-components';
  public static WebComponents = '@globalwebindex/platform2-web-components';
  public static AudienceBuilder = '@globalwebindex/platform2-audience-builder';
}

export interface IGwiPackageVersions {
  P2LIB_VERSION?: string;
  RUC_VERSION?: string;
  WEB_COMPONENTS_VERSION?: string;
  AUDIENCE_BUILDER_VERSION?: string;
}

export class MaintenancePages {
  public static Platform = 'platform-maintenance';
  public static MaintenanceSuffix = '-maintenance';
}

export interface SupportedMfes {
  'chart-builder': boolean;
  crosstabs: boolean;
  audiences: boolean;
  dashboards: boolean;
  'dashboards-react': boolean;
  'global-search': boolean;
  'tv-rf': boolean;
  insights: boolean;
  'account-settings': boolean;
  home: boolean;
  header: boolean;
}

export enum MicrofrontendName {
  CHART_BUILDER = 'chart-builder',
  AUDIENCES = 'audiences',
  DASHBOARDS = 'dashboards',
  DASHBOARDS_REACT = 'dashboards-react',
  INSIGHTS = 'insights',
  CROSSTABS = 'crosstabs',
  ACCOUNT_SETTINGS = 'account-settings',
  TV_STUDY = 'tv-study',
  TV_RF = 'tv-rf',
  GLOBAL_SEARCH = 'global-search',
  HOME = 'home',
  HEADER = 'header',
  BOOK_DEMO = 'book-demo'
}

export enum QuickLinkType {
  Chart = 'chart',
  Dashboard = 'dashboard',
  Category = 'category',
  HomePage = 'homepage'
}

export enum AnalyticsEvents {
  P2Visited = 'P2 Visited',
  FeedbackClicked = 'P2 - Feedback Button Clicked',
  ReturnToPlatform = 'P2 - Return to Current Platform',
  GotoApplicationCrosstabs = 'P2 - Crosstabs Opened',
  GotoApplicationDashboards = 'P2 - Dashboards Opened',
  GotoApplicationTvStudy = 'P2 - TV Study Opened',
  GotoApplicationInsights = 'P2 - Insights Opened',
  GotoApplicationCharts = 'P2 - Charts - App Opened',
  GotoApplicationAudiences = 'P2 - Audiences - App Opened',
  GotoApplicationAccountSettings = 'P2 - User Menu Opened',
  QuickLinkClicked = 'P2 - Homepage Quick Link clicked',
  OpenAccessDashboardsCTAclicked = 'P2 - Open Access Dashboards - CTA Clicked',
  OpenAccessDashboardsFormSubmitted = 'P2 - Open Access Dashboards - Form submitted',
  HomePageSearchSubmitted = 'P2 - Homepage - Search submitted',
  HomePageSearchBarClicked = 'P2 - Homepage - Search initiated',
  HomePageExampleClicked = 'P2 - Homepage - example selected',
  HomePageBetaLabelClicked = 'P2 - Homepage - Beta label clicked',
  ViewOnlyUpgradePlan = 'P2 - View Only - Upgrade Plan'
}

export interface AnalyticsP2VisitedPayload {
  event_name: AnalyticsEvents.P2Visited;
  plan_handle_snapshot: string;
  organisation_name_snapshot?: string;
  job_title_snapshot?: string;
  customer_features_snapshot?: string[];
  access_end_snapshot?: string;
  url?: string;
}

export interface AnalyticsFeedbackClickedPayload {
  event_name: AnalyticsEvents.FeedbackClicked;
  place: 'homepage';
}

export interface AnalyticsReturnToPlatformPayload {
  event_name: AnalyticsEvents.ReturnToPlatform;
}

export interface AnalyticsGotoAppPayload {
  event_name: AnalyticsEvents;
  place?: 'homepage' | 'header';
}

export interface AnalyticsViewOnlyUpgradePlanPayload {
  event_name: AnalyticsEvents.ViewOnlyUpgradePlan;
}

export interface AnalyticsOpenAccessDashboardsCTAclickedPayload {
  event_name: AnalyticsEvents;
  url: string;
}

export interface AnalyticsOpenAccessDashboardsFormSubmittedPayload {
  event_name: AnalyticsEvents;
  url: string;
}

export interface AnalyticZeitgeistQuickChartPayload {
  event_name: AnalyticsEvents.QuickLinkClicked;
  card_index: number;
  link: string;
  isNew: boolean;
  type: QuickLinkType;
  datasets: string[];
  app: string;
  question_codes: string[];
  question_names: string[];
  attributes_count?: number;
  locations: string[];
  locations_count?: number;
  waves: string[];
  waves_count?: number;
}

export interface KeyValuePair {
  [key: string]: any;
}

export interface AnalyticsIdentifyEvent {
  traits: KeyValuePair;
  app_name: string;
  user_id?: number;
  org_id?: number;
  user_email?: string;
}

export interface AnalyticsTrackEvent extends AnalyticsIdentifyEvent {
  properties: KeyValuePair;
  event_name: string;
  anonymous_id?: string;
  timestamp: string;
}

export interface AnalyticsHomePageSearchSubmittedPayload {
  event_name: AnalyticsEvents.HomePageSearchSubmitted;
  search_term: string;
  place: 'search_enter' | 'search_arrow';
  character_count: number;
}

export interface AnalyticsHomePageSearchBarClickedPayload {
  event_name: AnalyticsEvents.HomePageSearchBarClicked;
}

export interface AnalyticsHomePageExampleClickedPayload {
  event_name: AnalyticsEvents.HomePageExampleClicked;
  example_name: string;
}

export interface AnalyticsHomePageBetaLabelClickedPayload {
  event_name: AnalyticsEvents.HomePageBetaLabelClicked;
}

export type AnalyticsPayload =
  | AnalyticsP2VisitedPayload
  | AnalyticsFeedbackClickedPayload
  | AnalyticsReturnToPlatformPayload
  | AnalyticsGotoAppPayload
  | AnalyticZeitgeistQuickChartPayload
  | AnalyticsOpenAccessDashboardsCTAclickedPayload
  | AnalyticsOpenAccessDashboardsFormSubmittedPayload
  | AnalyticsHomePageSearchSubmittedPayload
  | AnalyticsHomePageSearchBarClickedPayload
  | AnalyticsHomePageExampleClickedPayload
  | AnalyticsHomePageBetaLabelClickedPayload;

export interface PopularQuestionsModel {
  namespace_code: string;
  name: string;
  question_code: string;
  category_id: number;
  isSelected: boolean;
}
export interface GetPopularQuestionsResponse {
  popular_questions: Question[];
}

export interface PostPopularQuestionsRequest {
  questions: Question[];
}

export interface PostPopularQuestionsResponse {}
